import { Box } from '@wix/design-system';
import { FunctionComponent, useContext, useEffect } from 'react';
import { clickOnAnswer } from '@wix/bi-logger-customer-care-data/v2';
import { ArticleSearchResultItem } from '../../../types';
import { DATA_HOOKS } from '../../../dataHooks';
import { BI } from '../../../constants';
import { useBI } from '../../../hooks/useBI';
import { useRouter } from 'next/router';
import { Context } from '../../../context';
import { countWords } from '../../../utils/text';
import { cleanGeneratedAnswer } from '../../../utils/bi';
import { GenAnswerHeader } from './GenAnswerHeader';
import { GenAnswerBody } from './GenAnswerBody';
import css from './index.module.scss';

export type GeneratedAnswerProps = {
  searchQuestion: string;
  searchAnswer: string | null;
  isProhibited: boolean | null;
  articleLinks: ArticleSearchResultItem[];
  retryGenerateAnswer: () => Promise<void>;
};

export enum unhelpfulFeedbackTypes {
  WRONG = 'wrong',
  IRRELEVANT = 'irrelevant',
  CONFUSING = 'confusing',
  HARD = 'hard',
  OUTDATED = 'outdated',
}
export const formatRichText = (text: string): string => {
  const parser = new DOMParser();
  // replace **text** with<strong>text</strong>
  const boldRegex = /\*\*(.*?)\*\*/g;
  const doc = parser.parseFromString(text.replace(boldRegex, '<strong>$1</strong>'), 'text/html');
  doc.querySelectorAll('a').forEach(aTag => {
    aTag.setAttribute('target', '_blank');
  });
  return doc.body.innerHTML;
};

export const GeneratedAnswer: FunctionComponent<GeneratedAnswerProps> = ({
  searchQuestion,
  searchAnswer,
  articleLinks,
  isProhibited,
  retryGenerateAnswer
}) => {
  const { sendBIEvent } = useBI();
  const { locale } = useRouter();
  const { searchSessionId } = useContext(Context);

  useEffect(() => {
    if (searchAnswer && searchAnswer?.length > 0) {
      const anchors = document.querySelectorAll(`[data-hook=${DATA_HOOKS.GEN_ANSWER_ANSWER}] a`);

      const moreInfoArticlesToJSON = () =>
        articleLinks.map((articleLink) => ({
          article_id: articleLink.id,
          output_distance: articleLink.distance,
        })
        );

      const handleClickLink = async (anchor: Event) => {
        const linkURL = (anchor.target as HTMLElement).getAttribute('href');
        await sendBIEvent(
          clickOnAnswer({
            question: searchQuestion,
            source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
            kb_lang: locale as string,
            clicked_url: linkURL as string,
            clicked_item_type: BI.ITEM_TYPES.SUMMARIZED_ANSWER_HYPERLINK,
            search_result_selection:
              BI.SEARCH_RESULT_SELECTION.FULL_SEARCH_RESULTS_LIST,
            search_method: BI.SEARCH_METHOD.KNOWLEDGE_AGENT,
            search_session_Id: searchSessionId,
            summarized_answer_based_on: JSON.stringify(moreInfoArticlesToJSON()),
            summarized_answer_words_count: countWords(searchAnswer || ''),
            summarized_answer_text: searchAnswer ? cleanGeneratedAnswer(searchAnswer) : undefined,
          })
        );
      };

      anchors.forEach((anchor) => {
        anchor.addEventListener('click', handleClickLink);
      });

      return () => {
        anchors.forEach((anchor) => {
          anchor.removeEventListener('click', handleClickLink);
        });
      };
    }
  }, [articleLinks, locale, searchAnswer, searchQuestion, searchSessionId, sendBIEvent]);


  return (
    <Box className={css.wrapper} dataHook={DATA_HOOKS.GEN_ANSWER_WRAPPER}>
      <Box direction="vertical" className={css.innerWrapper}>
        <Box direction="vertical">
          <GenAnswerHeader searchQuestion={searchQuestion} />
          <GenAnswerBody searchQuestion={searchQuestion} searchAnswer={searchAnswer} isProhibited={isProhibited} retryGenerateAnswer={retryGenerateAnswer} articleLinks={articleLinks} />
        </Box>
      </Box>
    </Box>);
};
